import { Component, inject, OnInit, OnDestroy, HostListener, ElementRef, ViewChild, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription, BehaviorSubject, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, catchError, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../core/Provider/auth.service';
import { SidebarService } from '../../core/Provider/sidebar.service';
import { User } from '../../core/models/user.model';
import { AssignedStudent } from '../../core/models/common.model';
import { Constants } from '../../core/constants/core.constants';
import { APP_CONSTANTS } from '../../core/constants/app.constants';
import { formatRoleLabel } from '../../core/utils/role-label.utils';
import { NotesModalComponent } from '../../features/students/view-student/notes-modal/notes-modal.component';
import { StudentComplaintComponent } from '../../features/student-dashboard/student-complaint/student-complaint.component';
import { InboxService } from '../../services/inbox.service';
import { StudentActivityTrackingService } from '../../services/student-activity-tracking.service';
import { StudentActivityType } from '../../features/students/student.model';
import { StudentService } from '../../services/student.service';
import { PhoneMaskPipe } from '../../pipes/phone-mask.pipe';
import { InboxItemListResponse } from '../../features/inbox/inbox.modal';
import { getInboxItemDisplayName, parseInboxPayload } from '../../features/inbox/inbox.utils';
import { ROLE_PERMISSIONS } from '../../core/permissions/role-permissions';
import { normalizeRoleNameForPermissions } from '../../core/utils/role-normalization.utils';
import { cseNoteReminderCheck$, cseNoteReminderOpen$ } from './cse-note-reminder';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    AvatarModule,
    InputTextModule,
    TooltipModule,
    NotesModalComponent,
    StudentComplaintComponent,
    PhoneMaskPipe
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('searchDropdown', { static: false }) searchDropdown?: ElementRef;
  @ViewChild('searchIconBtn', { static: false }) searchIconBtn?: ElementRef;
  @ViewChild('desktopSearchContainer', { static: false }) desktopSearchContainer?: ElementRef;
  @ViewChild('userProfileDropdown', { static: false }) userProfileDropdown?: ElementRef;
  @ViewChild('userProfileBtn', { static: false }) userProfileBtn?: ElementRef;
  @ViewChild('notificationMenu', { static: false }) notificationMenu?: ElementRef;

  private authService = inject(AuthService);
  private sidebarService = inject(SidebarService);
  private router = inject(Router);
  private inboxService = inject(InboxService);
  private activityTracking = inject(StudentActivityTrackingService);
  private studentService = inject(StudentService);
  private destroyRef = inject(DestroyRef);
  private subscription = new Subscription();
  private readonly searchInput$ = new Subject<string>();

  searchQuery = '';
  searchResults: AssignedStudent[] = [];
  isSearchLoading = false;
  isSearchResultsOpen = false;
  searchIcon = Constants.search;
  cancelIcon = Constants.cancelIcon;
  chevronDownIcon = Constants.chevronDown;
  sidebarToggleIcon = Constants.sidebarExpandDark;
  logo = Constants.logoBlack;
  inboxEmptyIcon = Constants.gridSmsIcon;
  notificationAvatarIcon = Constants.notificationWhite;
  addNotesIcon = Constants.addNotesIcon;
  readonly addNotesTooltip = 'Add Notes';
  inboxUnreadCount = 0;
  notificationUnreadCount = 0;
  isNotificationPreviewOpen = false;
  notificationPreviewLoading = false;
  notificationPreview: InboxItemListResponse[] = [];
  isSidebarCollapsed = false;
  isMobile = false;
  isSearchMobile = false;
  isSearchDropdownOpen = false;
  isUserProfileDropdownOpen = false;
  isNotesModalOpen = false;
  isComplaintModalOpen = false;
  isMobileProfile = false;
  isStudentDashboard = false;
  isStaffPortalPreview = false;
  private readonly BREAKPOINT = 1200;
  private readonly SEARCH_BREAKPOINT = 768;
  private readonly PROFILE_BREAKPOINT = 576;
  private readonly SEARCH_PAGE_SIZE = 8;

  ngOnInit(): void {
    this.checkWindowSize();
    this.checkCurrentRoute();
    this.loadUserData();
    this.loadHeaderCounts();
    this.setupStudentSearch();
    this.subscription.add(
      this.inboxService.countsUpdated$.subscribe(() => this.loadHeaderCounts())
    );
    this.subscription.add(
      this.sidebarService.isCollapsed$.subscribe(collapsed => {
        this.isSidebarCollapsed = collapsed;
        this.updateToggleIcon();
      })
    );
    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.isNotificationPreviewOpen = false;
        this.checkCurrentRoute();
        this.loadHeaderCounts();
        this.loadUserData();
        this.clearSearch();
      })
    );
    this.subscription.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUserSubject.next(user);
        if (!user) {
          this.inboxUnreadCount = 0;
          this.notificationUnreadCount = 0;
        }
      })
    );
    this.subscription.add(
      cseNoteReminderOpen$.subscribe(() => this.onAddNotesClick())
    );
    cseNoteReminderCheck$.next();
  }

  private setupStudentSearch(): void {
    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          const trimmed = query.trim();
          if (!trimmed) {
            this.searchResults = [];
            this.isSearchLoading = false;
            this.isSearchResultsOpen = false;
            return of([] as AssignedStudent[]);
          }
          this.isSearchLoading = true;
          this.isSearchResultsOpen = true;
          return this.studentService
            .getAssignedStudents(
              { StudentName: trimmed, Page: 1, PageSize: this.SEARCH_PAGE_SIZE },
              { skipLoader: true }
            )
            .pipe(
              catchError(() => of(null)),
              map((response) => {
                const data = response?.output?.data ?? response?.data;
                return (Array.isArray(data) ? data : []).map((row) => this.mapSearchStudent(row));
              })
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((students) => {
        this.searchResults = students;
        this.isSearchLoading = false;
      });
  }

  get canUseGlobalSearch(): boolean {
    return !this.isStudentDashboard && !this.isStudentRole(this.currentUserSubject.value);
  }

  get hasSearchValue(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  onSearchQueryChange(value: string): void {
    this.searchQuery = value;
    this.searchInput$.next(value);
  }

  onSearchFocus(): void {
    if (this.searchQuery.trim()) {
      this.isSearchResultsOpen = true;
    }
  }

  onClearSearch(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.clearSearch();
    this.searchInput$.next('');
  }

  private mapSearchStudent(row: Record<string, unknown> | AssignedStudent): AssignedStudent {
    const raw = row as Record<string, unknown>;
    const pick = (...keys: string[]) => {
      for (const key of keys) {
        const value = raw[key];
        if (value != null && String(value).trim() !== '') {
          return String(value).trim();
        }
      }
      return '';
    };

    return {
      studentId: pick('studentId', 'StudentId'),
      studentName: pick('studentName', 'StudentName'),
      email: pick('email', 'Email'),
      mobileNo: pick('mobileNo', 'MobileNo', 'marketingCellNumber', 'MarketingCellNumber') || null,
      lifeCycleStatusName: pick('lifeCycleStatusName', 'LifeCycleStatusName') || null
    };
  }

  getStatusColor = (status: string) => Constants.getStatusColor(status);
  getStatusBgColor = (status: string) => Constants.getStatusBgColor(status);

  onSelectStudent(student: AssignedStudent): void {
    if (!student?.studentId) {
      return;
    }
    this.clearSearch();
    this.closeSearchDropdown();
    this.router.navigate(['/students/view', student.studentId]);
  }

  private clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.isSearchLoading = false;
    this.isSearchResultsOpen = false;
  }

  private checkCurrentRoute(): void {
    this.isStudentDashboard = this.router.url.includes('/student-dashboard');
    this.isStaffPortalPreview = this.authService.isStaffPortalPreviewActive();
    if (this.isStaffPortalPreview) {
      this.isUserProfileDropdownOpen = false;
    }
  }

  get showHeaderInbox(): boolean {
    if (!this.authService.getToken()) {
      return false;
    }
    if (this.isStudentDashboard) {
      return true;
    }
    const role = normalizeRoleNameForPermissions(this.currentUserSubject.value?.roleName);
    return ROLE_PERMISSIONS.INBOX.includes(role);
  }

  get usesStudentInbox(): boolean {
    return this.isStudentDashboard || this.isStudentRole(this.currentUserSubject.value);
  }

  get inboxRoute(): string {
    return this.routeFor(APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD_INBOX, APP_CONSTANTS.ROUTES.INBOX);
  }

  get notificationRoute(): string {
    return this.routeFor(APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD_NOTIFICATIONS, APP_CONSTANTS.ROUTES.NOTIFICATIONS);
  }

  get isNotificationsRoute(): boolean {
    return this.router.url.split('?')[0] === this.notificationRoute;
  }

  toggleNotificationPreview(event: Event): void {
    event.stopPropagation();
    this.isNotificationPreviewOpen = !this.isNotificationPreviewOpen;
    if (this.isNotificationPreviewOpen) {
      this.activityTracking.track(StudentActivityType.OpenNotificationPreview, { pageName: 'Notifications' });
      this.loadNotificationPreview();
    }
  }

  previewName(item: InboxItemListResponse): string {
    const name = getInboxItemDisplayName(item, parseInboxPayload(item.payloadJson)).trim();
    return name || 'Notification';
  }

  openNotification(item: InboxItemListResponse): void {
    this.isNotificationPreviewOpen = false;
    this.router.navigate([this.notificationRoute], {
      queryParams: { item: String(item.inboxItemReceiverId) }
    });
  }

  openAllNotifications(): void {
    this.isNotificationPreviewOpen = false;
    this.router.navigate([this.notificationRoute]);
  }

  private routeFor(studentRoute: string, staffRoute: string): string {
    return this.usesStudentInbox ? studentRoute : staffRoute;
  }

  private loadNotificationPreview(): void {
    this.notificationPreviewLoading = true;
    this.subscription.add(
      this.inboxService.getRecentNotifications(3).subscribe((items) => {
        this.notificationPreview = items;
        this.notificationPreviewLoading = false;
      })
    );
  }

  private loadHeaderCounts(): void {
    if (!this.showHeaderInbox) {
      this.inboxUnreadCount = 0;
      this.notificationUnreadCount = 0;
      return;
    }

    this.subscription.add(
      this.inboxService.getInboxHeaderCounts().subscribe({
        next: (counts) => {
          this.inboxUnreadCount = counts.action;
          this.notificationUnreadCount = counts.notifications;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.checkWindowSize();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (this.isSearchMobile && this.isSearchDropdownOpen &&
        !this.isClickInside(this.searchDropdown, target) &&
        !this.isClickInside(this.searchIconBtn, target)) {
      this.closeSearchDropdown();
      this.clearSearch();
    }

    if (!this.isSearchMobile && this.isSearchResultsOpen &&
        !this.isClickInside(this.desktopSearchContainer, target)) {
      this.isSearchResultsOpen = false;
    }

    if (this.isUserProfileDropdownOpen &&
        !this.isClickInside(this.userProfileDropdown, target) &&
        !this.isClickInside(this.userProfileBtn, target)) {
      this.closeUserProfileDropdown();
    }

    if (this.isNotificationPreviewOpen && !this.isClickInside(this.notificationMenu, target)) {
      this.isNotificationPreviewOpen = false;
    }
  }

  private isClickInside(elementRef: ElementRef | undefined, target: HTMLElement): boolean {
    return elementRef?.nativeElement?.contains(target) ?? false;
  }

  private checkWindowSize(): void {
    this.isMobile = window.innerWidth < this.BREAKPOINT;
    this.isSearchMobile = window.innerWidth < this.SEARCH_BREAKPOINT;
    this.isMobileProfile = window.innerWidth < this.PROFILE_BREAKPOINT;
    this.updateToggleIcon();
    if (!this.isSearchMobile && this.isSearchDropdownOpen) {
      this.isSearchDropdownOpen = false;
    }
  }

  private updateToggleIcon(): void {
    this.sidebarToggleIcon = this.isSidebarCollapsed
      ? Constants.sidebarExpandDark
      : Constants.sidebarCollapse;
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  toggleSearchDropdown(): void {
    this.isSearchDropdownOpen = !this.isSearchDropdownOpen;
    if (!this.isSearchDropdownOpen) {
      this.clearSearch();
    }
  }

  closeSearchDropdown(): void {
    this.isSearchDropdownOpen = false;
    this.isSearchResultsOpen = false;
  }

  toggleUserProfileDropdown(): void {
    if (this.isStaffPortalPreview) {
      this.isUserProfileDropdownOpen = false;
      return;
    }
    this.isUserProfileDropdownOpen = !this.isUserProfileDropdownOpen;
  }

  closeUserProfileDropdown(): void {
    this.isUserProfileDropdownOpen = false;
  }

  onMenuItemClick(action: string): void {
    this.closeUserProfileDropdown();
    if (action === 'logout' && this.isStaffPortalPreview) {
      return;
    }

    switch (action) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'complaint':
        if (this.isStaffPortalPreview) {
          return;
        }
        this.isComplaintModalOpen = true;
        break;
      case 'logout':
        this.logout();
        break;
    }
  }

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();

  private loadUserData(): void {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN);

    if (!token) {
      this.currentUserSubject.next(null);
      return;
    }

    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const userDataStr = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.USER_DATA);
    if (!userDataStr) {
      const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN);
      if (token) {
        const userFromToken = this.getUserFromToken(token);
        if (userFromToken) {
          this.setUser(userFromToken);
        }
      }
      return;
    }

    try {
      const userData = JSON.parse(userDataStr);
      const user = this.mapStorageDataToUser(userData);
      if (user) {
        this.setUser(user);
      }
    } catch {
      /* invalid stored user JSON */
    }
  }

  private setUser(user: User): void {
    this.currentUserSubject.next(user);
    this.authService.setCurrentUser(user);
  }

  private getUserFromToken(token: string): User | null {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded) return null;

      const roleName = decoded.roleDisplayName || '';

      return {
        id: decoded.sub || decoded.userId || '',
        email: decoded.email || '',
        fullName: (decoded.unique_name || '').trim(),
        roleName: roleName,
        roleDisplayName: roleName === 'CSI' ? 'CSA / CSI' : roleName,
        avatar: undefined,
        phone: undefined,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  private decodeToken(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  private mapStorageDataToUser(storageData: any): User | null {
    if (!storageData) {
      return null;
    }

    const getValue = (...keys: string[]): string | undefined => {
      for (const key of keys) {
        const value = storageData[key];
        if (value !== null && value !== undefined && value !== '') {
          return String(value);
        }
      }
      return undefined;
    };

    const getDate = (key: string): Date => {
      const value = storageData[key];
      return value ? new Date(value) : new Date();
    };

    return {
      id: storageData.id || getValue('UserId', 'userId') || '',
      email: storageData.email || getValue('Email') || '',
      fullName: storageData.fullName || getValue('FullName')?.trim(),
      roleName: storageData.roleName || getValue('Role', 'roleDisplayName', 'RoleName')?.trim(),
      roleDisplayName: storageData.roleDisplayName || getValue('RoleDisplayName')?.trim(),
      avatar: storageData.avatar || getValue('profileImage'),
      phone: storageData.phone || getValue('mobileNo'),
      studentId: getValue('studentId'),
      joiningDate: getValue('joiningDate'),
      managerName: getValue('managerName'),
      createdAt: getDate('createdAt'),
      updatedAt: getDate('updatedAt')
    };
  }

  isStudentRole(user: User | null): boolean {
    const role = (user?.roleName || '').trim();
    if (!role) return false;
    return role.toLowerCase() === APP_CONSTANTS.ROLES.STUDENT.toLowerCase();
  }

  get canAddNotes(): boolean {
    return !this.isStudentDashboard && !this.isStudentRole(this.currentUserSubject.value);
  }

  onAddNotesClick(): void {
    this.closeUserProfileDropdown();
    this.isNotesModalOpen = true;
  }

  getDisplayRole(user: User | null): string {
    if (!user) return '';

    if (user.roleDisplayName && user.roleDisplayName.trim().length > 0) {
      return user.roleDisplayName;
    }

    return formatRoleLabel(user.roleName);
  }

  getInitials(user: User): string {
    const fullName = user.fullName || '';
    if (!fullName) return 'U';
    const names = fullName.trim().split(' ').filter(n => n);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    } else if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return 'U';
  }

  logout(): void {
    this.authService.logout().subscribe(() => {
      this.currentUserSubject.next(null);
      this.router.navigate([APP_CONSTANTS.ROUTES.LOGIN]);
    });
  }
}
